"use client";

import { useMutation } from "@apollo/client";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Loader2, Trash2, X } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useTheme } from "next-themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AvatarWithInitials from "components/core/AvatarWithInitials/AvatarWithInitials";
import { LiquidPill } from "components/ui/v2/liquid-pill";
import { LiquidPillGroup } from "components/ui/v2/liquid-pill-group";
import { Modal } from "components/ui/v2/modal";
import { ReactionBurstOverlay, useReactionBurst } from "components/ui/v2/explore/reaction-burst-overlay";
import { StoryViewCounterV2 } from "components/ui/v2/explore/StoryViewCounterV2";
import { Z_INDEX } from "components/ui/v2/z-index";
import { LinkPreviewV2 } from "components/ui/v2/link-preview-v2";
import { gqlMessage } from "gql";
import { REACTION_EMOJI, REACTION_NAMES, reactionEmoji } from "utils/reactionEmoji";
import type { IMessage, IMessageReaction } from "types/Message.type";

const getStoryVisibleUser = (story?: IMessage) => story?.ownerUser || story?.createdBy;

export interface StoryGroup {
  userId: string;
  user?: IMessage["createdBy"];
  latestStory: IMessage;
  stories: IMessage[];
}

export interface StoryViewerModalProps {
  storyGroups: StoryGroup[];
  initialGroupIndex: number;
  currentUserId?: string;
  onClose: () => void;
  onReact: (storyId: string, emoji: string) => void | Promise<void>;
  onDelete?: (storyId: string) => void | Promise<void>;
}

/** Sentinel emoji value the backend interprets as "remove my reaction". */
const CLEAR_REACTION = "none";
const STORY_DURATION_MS = 5000;
const STORY_VIEW_DELAY_MS = 1000;

const REACTION_VARIANTS: Record<string, "ocean" | "rose" | "gold" | "sunset" | "sky" | "coral"> = {
  like: "ocean",
  love: "rose",
  haha: "gold",
  wow: "sunset",
  sad: "sky",
  angry: "coral",
};

const fullName = (person?: { name?: string; lastname?: string }) =>
  `${person?.name || ""} ${person?.lastname || ""}`.trim();

export function StoryViewerModal({
  storyGroups,
  initialGroupIndex,
  currentUserId,
  onClose,
  onReact,
  onDelete,
}: StoryViewerModalProps) {
  const { t } = useTranslation("explore");
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [groupIndex, setGroupIndex] = useState(initialGroupIndex);
  const [storyIndex, setStoryIndex] = useState(0);
  const [openEmoji, setOpenEmoji] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const viewerRef = useRef<HTMLDivElement>(null);
  const viewedStoriesRef = useRef<Set<string>>(new Set());
  const { reactionBurst, triggerReactionBurst } = useReactionBurst();
  const [localViewsCount, setLocalViewsCount] = useState<number | undefined>(undefined);

  const [addViewToStory] = useMutation(gqlMessage.mutations.ADD_VIEW_TO_STORY, {
    errorPolicy: "ignore",
  });

  // Track image loading state per story so we can show an overlay while the
  // new thumbnail downloads (otherwise the browser keeps the previous one
  // visible, which looks like the viewer is stuck on the old story).
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => setMounted(true), []);

  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const clampedGroupIndex = Math.min(
    Math.max(groupIndex, 0),
    Math.max(storyGroups.length - 1, 0)
  );
  const activeGroup = storyGroups[clampedGroupIndex];
  const clampedStoryIndex = Math.min(
    Math.max(storyIndex, 0),
    Math.max((activeGroup?.stories.length ?? 1) - 1, 0)
  );
  const activeStory = activeGroup?.stories[clampedStoryIndex];
  const isLast =
    clampedGroupIndex >= storyGroups.length - 1 &&
    clampedStoryIndex >= (activeGroup?.stories.length ?? 1) - 1;

  const goPrev = useCallback(() => {
    setOpenEmoji(null);

    if (clampedStoryIndex > 0) {
      setStoryIndex(clampedStoryIndex - 1);
      return;
    }

    if (clampedGroupIndex > 0) {
      const previousGroupIndex = clampedGroupIndex - 1;
      const previousGroup = storyGroups[previousGroupIndex];
      setGroupIndex(previousGroupIndex);
      setStoryIndex(Math.max((previousGroup?.stories.length ?? 1) - 1, 0));
    }
  }, [clampedGroupIndex, clampedStoryIndex, storyGroups]);

  const goNext = useCallback(() => {
    setOpenEmoji(null);

    if (clampedStoryIndex < (activeGroup?.stories.length ?? 1) - 1) {
      setStoryIndex(clampedStoryIndex + 1);
      return;
    }

    if (clampedGroupIndex < storyGroups.length - 1) {
      setGroupIndex(clampedGroupIndex + 1);
      setStoryIndex(0);
    }
  }, [activeGroup?.stories.length, clampedGroupIndex, clampedStoryIndex, storyGroups.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext]);

  useEffect(() => {
    if (!activeStory) return;

    const timeout = window.setTimeout(() => {
      if (isLastRef.current) {
        onCloseRef.current();
        return;
      }

      goNextRef.current();
    }, STORY_DURATION_MS);

    return () => window.clearTimeout(timeout);
  }, [activeStory?.id]);

  const grouped = useMemo(() => {
    const map: Record<string, IMessageReaction[]> = {};
    (activeStory?.reactions || []).forEach((reaction) => {
      (map[reaction.emoji] ||= []).push(reaction);
    });
    return map;
  }, [activeStory]);

  const myReaction = useMemo(
    () => activeStory?.reactions?.find((r) => r.user === currentUserId)?.emoji ?? null,
    [activeStory, currentUserId]
  );

  const activeStoryUser = getStoryVisibleUser(activeStory);
  const sharedPostUrl = useMemo(() => {
    if (activeStory?.linkUrl) return activeStory.linkUrl;
    if (activeStory?.sourceMessageId && typeof window !== "undefined") {
      return `${window.location.origin}/app/thread/${activeStory.sourceMessageId}`;
    }
    return null;
  }, [activeStory?.linkUrl, activeStory?.sourceMessageId]);
  const isSharedPostStory = Boolean(activeStory?.sourceMessageId && sharedPostUrl);
  const isOwnStory = activeStoryUser?.id === currentUserId;

  useEffect(() => {
    setLocalViewsCount(activeStory?.uniqueViewsCount);
  }, [activeStory?.id, activeStory?.uniqueViewsCount]);

  useEffect(() => {
    if (!activeStory?.id || isOwnStory) return;
    if (viewedStoriesRef.current.has(activeStory.id)) return;

    const hasThumbnail = Boolean(activeStory.thumbnail);
    if (hasThumbnail && !imageLoaded) return;

    const timeout = window.setTimeout(() => {
      if (viewedStoriesRef.current.has(activeStory.id)) return;
      viewedStoriesRef.current.add(activeStory.id);

      addViewToStory({
        variables: { storyId: activeStory.id },
        update: (cache, { data }) => {
          if (!data?.addViewToStory) return;

          try {
            const existing = cache.readQuery<{ getStories: IMessage[] }>({
              query: gqlMessage.queries.GET_STORIES,
            });
            if (!existing?.getStories) return;

            cache.writeQuery({
              query: gqlMessage.queries.GET_STORIES,
              data: {
                getStories: existing.getStories.map((story) =>
                  story.id === activeStory.id
                    ? {
                        ...story,
                        uniqueViewsCount: (story.uniqueViewsCount ?? 0) + 1,
                        viewedByMe: true,
                      }
                    : story
                ),
              },
            });
          } catch {
            // Cache miss — parent refetch will sync eventually.
          }
        },
      })
        .then(({ data }) => {
          if (data?.addViewToStory) {
            setLocalViewsCount((current) => (current ?? activeStory.uniqueViewsCount ?? 0) + 1);
          }
        })
        .catch(() => undefined);
    }, STORY_VIEW_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [
    activeStory?.id,
    activeStory?.thumbnail,
    activeStory?.uniqueViewsCount,
    addViewToStory,
    imageLoaded,
    isOwnStory,
  ]);

  const isLastRef = useRef(isLast);
  const goNextRef = useRef(goNext);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    isLastRef.current = isLast;
    goNextRef.current = goNext;
    onCloseRef.current = onClose;
  }, [goNext, isLast, onClose]);

  const handleDelete = useCallback(async () => {
    if (!activeStory || !onDelete) return;

    try {
      setDeleteLoading(true);
      await onDelete(activeStory.id);
      setDeleteConfirmOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  }, [activeStory, onDelete]);

  const handleSelect = useCallback(
    (name: string) => {
      if (!activeStory) return;
      const shouldClearReaction = name === myReaction;

      if (!shouldClearReaction) {
        triggerReactionBurst(name, viewerRef.current, { resolveSurface: false });
      }

      // Tapping your current reaction again clears it.
      onReact(activeStory.id, shouldClearReaction ? CLEAR_REACTION : name);
    },
    [activeStory, myReaction, onReact, triggerReactionBurst]
  );

  useEffect(() => {
    setStoryIndex((currentStoryIndex) =>
      Math.min(currentStoryIndex, Math.max((activeGroup?.stories.length ?? 1) - 1, 0))
    );
  }, [activeGroup?.stories.length]);

  // Reset image-loaded state whenever the visible story changes. If the
  // browser cache already has the image, the <img>'s onLoad fires synchronously
  // and flips this back to true on the next tick.
  useEffect(() => {
    setImageLoaded(false);
  }, [activeStory?.id, activeStory?.thumbnail]);

  if (!mounted || !activeStory) return null;

  const whoReacted = openEmoji ? grouped[openEmoji] || [] : [];

  return createPortal(
    <div
      className={`fixed inset-0 flex items-center justify-center bg-black/90 backdrop-blur-sm${resolvedTheme === "dark" ? " dark" : ""}`}
      style={{ zIndex: Z_INDEX.MODAL }}
      role="dialog"
      aria-modal="true"
      aria-label={t("Visor de historia")}
      onClick={onClose}
    >
      <div
        ref={viewerRef}
        className="relative flex h-full max-h-[92vh] w-full max-w-[460px] flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <style jsx global>{`
          @keyframes explore-story-progress-fill {
            from { transform: scaleX(0); }
            to { transform: scaleX(1); }
          }
        `}</style>

        <ReactionBurstOverlay burst={reactionBurst} zIndex={Z_INDEX.MODAL + 1} />

        {/* Progress segments — scoped to the active person's stories */}
        <div className="absolute left-3 right-3 top-3 z-20 flex gap-1">
          {activeGroup.stories.map((story, i) => (
            <span
              key={story.id}
              className="relative h-1 flex-1 overflow-hidden rounded-full bg-white/30"
            >
              {i < clampedStoryIndex && <span className="absolute inset-0 bg-white" />}
              {i === clampedStoryIndex && (
                <span
                  key={`${story.id}-${clampedGroupIndex}-${clampedStoryIndex}`}
                  className="absolute inset-y-0 left-0 w-full origin-left bg-white"
                  style={{
                    animation: `explore-story-progress-fill ${STORY_DURATION_MS}ms linear forwards`,
                  }}
                />
              )}
            </span>
          ))}
        </div>

        {/* Top bar: author + close */}
        <div className="absolute left-0 right-0 top-0 z-20 flex items-start justify-between gap-2 bg-gradient-to-b from-black/60 to-transparent px-3 pb-8 pt-6">
          <div className="flex items-center gap-2">
            <AvatarWithInitials
              urlImg={activeStoryUser?.profileImage}
              initials={fullName(activeStoryUser)}
              size="sm"
            />
            <div className="leading-tight">
              <p className="text-sm font-semibold text-white">
                {fullName(activeStoryUser)}
              </p>
              <p className="text-[11px] text-white/70">
                {formatDistanceToNow(new Date(activeStory.createdAt), {
                  addSuffix: true,
                  locale: es,
                })}
                {(activeGroup?.stories.length ?? 0) > 1 && (
                  <span> · {clampedStoryIndex + 1}/{activeGroup.stories.length}</span>
                )}
              </p>
            </div>
          </div>
          <div className="mt-2 flex shrink-0 flex-col items-center gap-1.5">
            <LiquidPill
              as="button"
              type="button"
              shape="circle"
              size="sm"
              variant="slate"
              appearance="normal"
              reactive
              hoverBounce
              hoverDeform
              shadow="neutral"
              icon={<X className="h-4 w-4" />}
              onClick={onClose}
              aria-label={t("Cerrar")}
            />
            {isOwnStory && onDelete && (
              <LiquidPill
                as="button"
                type="button"
                shape="circle"
                size="sm"
                variant="rose"
                appearance="normal"
                reactive
                hoverBounce
                hoverDeform
                shadow="neutral"
                icon={<Trash2 className="h-3.5 w-3.5" />}
                onClick={() => setDeleteConfirmOpen(true)}
                aria-label={t("Eliminar story")}
              />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="relative flex flex-1 items-center justify-center overflow-hidden pointer-events-none">
          {isSharedPostStory && sharedPostUrl ? (
            <>
              {activeStory.thumbnail ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={activeStory.thumbnail}
                  alt=""
                  aria-hidden
                  className="absolute inset-0 h-full w-full scale-110 object-cover opacity-35 blur-2xl"
                />
              ) : null}
              <div className="relative z-[15] mx-auto w-full max-w-sm px-4 pointer-events-auto">
                <LinkPreviewV2
                  url={sharedPostUrl}
                  variant="story"
                  initialPreview={{
                    title: activeStory.previewTitle,
                    description: activeStory.previewDescription,
                    image: activeStory.thumbnail,
                    url: sharedPostUrl,
                  }}
                />
              </div>
            </>
          ) : activeStory.thumbnail ? (
            <>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                key={activeStory.id}
                src={activeStory.thumbnail}
                alt={fullName(activeStoryUser) || t("Historia")}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-150 ${
                  imageLoaded ? "opacity-100" : "opacity-0"
                }`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(true)}
              />
              {!imageLoaded && (
                <div className="absolute inset-0 z-[5] flex items-center justify-center bg-black/55 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-white/90" />
                    <p className="text-xs font-medium text-white/80">{t("Cargando imagen...")}</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="px-6 text-center text-sm text-white/70">{t("Sin imagen")}</div>
          )}
          {/* ponytail: IG-style tap nav — left third = prev, right two-thirds = next (closes on last) */}
          <button
            type="button"
            onClick={goPrev}
            aria-label={t("Anterior")}
            className="absolute inset-y-0 left-0 z-[15] w-1/3 cursor-pointer focus:outline-none pointer-events-auto"
          />
          <button
            type="button"
            onClick={isLast ? onClose : goNext}
            aria-label={isLast ? t("Cerrar") : t("Siguiente")}
            className="absolute inset-y-0 right-0 z-[15] w-2/3 cursor-pointer focus:outline-none pointer-events-auto"
          />
        </div>

        {/* Prev / Next */}
        {(clampedGroupIndex > 0 || clampedStoryIndex > 0) && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              goPrev();
            }}
            aria-label={t("Anterior")}
            className="absolute left-1 top-1/2 z-30 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white transition hover:bg-black/60 pointer-events-auto"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {!isLast && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              goNext();
            }}
            aria-label={t("Siguiente")}
            className="absolute right-1 top-1/2 z-30 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white transition hover:bg-black/60 pointer-events-auto"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}

        {/* Bottom: reactions */}
        <div className="absolute bottom-0 left-0 right-0 z-20 flex flex-col gap-2 bg-gradient-to-t from-black/70 to-transparent px-3 pb-4 pt-10">
          {/* Who-reacted panel */}
          {openEmoji && (
            <div className="max-h-40 overflow-y-auto rounded-2xl bg-white/95 p-2 dark:bg-zinc-900/95">
              {whoReacted.length === 0 ? (
                <p className="px-2 py-1 text-xs text-slate-500">{t("Nadie aún")}</p>
              ) : (
                whoReacted.map((reaction) => (
                  <div
                    key={`${reaction.user}-${reaction.emoji}`}
                    className="flex items-center gap-2 px-1 py-1"
                  >
                    <AvatarWithInitials
                      urlImg={reaction.reactionUser?.profileImage}
                      initials={fullName(reaction.reactionUser)}
                      size="xs"
                    />
                    <span className="text-xs text-slate-700 dark:text-zinc-200">
                      {fullName(reaction.reactionUser) || t("Usuario")}
                    </span>
                    <span className="ml-auto text-sm">{reactionEmoji(reaction.emoji)}</span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Counters */}
          <div className="flex flex-wrap items-center gap-1.5">
            {activeStory?.id ? (
              <StoryViewCounterV2
                count={localViewsCount}
                storyId={activeStory.id}
              />
            ) : null}
            {Object.entries(grouped).map(([emoji, list]) => (
              <button
                key={emoji}
                type="button"
                onClick={() => setOpenEmoji((cur) => (cur === emoji ? null : emoji))}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                  openEmoji === emoji
                    ? "bg-white text-slate-900"
                    : "bg-white/90 text-slate-800 hover:bg-white"
                }`}
                aria-label={`${list.length} ${t("reacciones")}`}
              >
                <span className="text-sm">{reactionEmoji(emoji)}</span>
                {list.length}
              </button>
            ))}
            {Object.keys(grouped).length === 0 && (
              <span className="text-xs text-white/70">{t("Aún sin reacciones")}</span>
            )}
          </div>

          {/* Reaction picker (not on your own story) */}
          {!isOwnStory && (
            <div className="self-center rounded-full border border-white/15 bg-white/20 px-1 py-0.5 shadow-lg backdrop-blur-md dark:border-white/10 dark:bg-black/20">
              <LiquidPillGroup
                activeValue={myReaction}
                size="sm"
                className="gap-0.5 [&_.lpg-item]:h-7 [&_.lpg-item]:w-7 [&_.lpg-item]:flex [&_.lpg-item]:items-center [&_.lpg-item]:justify-center [&_.lpg-item]:px-0 [&_.lpg-item]:py-0 [&_.lpg-item>span]:flex [&_.lpg-item>span]:h-full [&_.lpg-item>span]:w-full [&_.lpg-item>span]:items-center [&_.lpg-item>span]:justify-center [&_.lpg-item>span]:leading-none"
                items={REACTION_NAMES.map((emoji) => ({
                  value: emoji,
                  icon: (
                    <span className="flex h-full w-full items-center justify-center text-[15px] leading-none">
                      {REACTION_EMOJI[emoji]}
                    </span>
                  ),
                  variant: REACTION_VARIANTS[emoji],
                }))}
                onChange={handleSelect}
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        open={deleteConfirmOpen}
        onClose={() => {
          if (!deleteLoading) setDeleteConfirmOpen(false);
        }}
        size="xs"
        backdrop="static"
        title={t("Eliminar story")}
        description={t("Esta acción no se puede deshacer.")}
        footer={
          <div className="flex w-full justify-end gap-2">
            <LiquidPill
              as="button"
              type="button"
              variant="slate"
              appearance="normal"
              text={t("Cancelar")}
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={deleteLoading}
            />
            <LiquidPill
              as="button"
              type="button"
              variant="rose"
              appearance="tinted"
              icon={deleteLoading ? undefined : <Trash2 className="h-3.5 w-3.5" />}
              text={deleteLoading ? t("Eliminando...") : t("Eliminar")}
              onClick={handleDelete}
              disabled={deleteLoading}
            />
          </div>
        }
      >
        <p className="px-4 py-2 text-sm text-slate-600 dark:text-zinc-300">
          {t("¿Seguro que quieres eliminar esta story?")}
        </p>
      </Modal>
    </div>,
    document.body
  );
}
